// /////////////////////////////////////////////////////////////
// [ファイル] agentSettingsShared.tsx
// [概要] エージェント設定ダイアログ (AssistantAgentDialog) の各ペインが共用する定数・判定関数・小部品。
//        - 注入文字数の数え方はバックエンド (assistant_agent_store.ts loadAgentDefinitions) と同じにする
//          (有効なルール / スキル / 未完了の課題の 名前 + 説明 + 本文。ワークフローは含めない)
//        - 見える人 (audience) の判定は定義のグループ有無で分ける (グループ付きはグループの共有先)
//        - 保存バー・詳細ヘッダー・項目行など、定義面とグループ面で同じ見た目にしたい部品をここに置く
// /////////////////////////////////////////////////////////////
import React, { useMemo } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { AnimatePresence, motion } from 'framer-motion';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { useMasterData } from '../../providers/MasterDataProvider';
import type { AgentGroup, AgentItem, AgentType, IssueStatus } from '../../hooks/useAssistantAgents';

// 注入サイズの警告閾値 (バックエンドの INJECTION_WARN_CHARS と同値)
export const INJECTION_LIMIT = 16000;

// 左レールの区分。定義 4 種のほか、グループ面と自動処理面を持つ
export type Section = AgentType | 'groups' | 'automation';
// 一覧の絞り込み
export type ListFilter = 'all' | 'mine' | 'shared' | 'off';

export const AGENT_TYPES: AgentType[] = ['rule', 'skill', 'workflow', 'issue'];

export const KIND_LABEL: Record<AgentType, string> = {
  rule: 'ルール',
  skill: 'スキル',
  workflow: 'ワークフロー',
  issue: '取組み課題',
};

export const KIND_HINT: Record<AgentType, string> = {
  rule: '常に従う指示',
  skill: '該当する依頼で従う手順書',
  workflow: '実行時に送る定型プロンプト',
  issue: '未完了は AI が会話のたびに参照',
};

export const BODY_LABEL: Record<AgentType, string> = {
  rule: '本文 (常に従う指示)',
  skill: '本文 (手順)',
  workflow: '本文 (送信する定型プロンプト)',
  issue: '本文 (背景・現状・進捗)',
};

export const DESC_LABEL: Record<AgentType, string> = {
  rule: '説明 (任意)',
  skill: '用途の説明 (AI が該当する依頼か判断する材料)',
  workflow: '用途の説明',
  issue: '課題の概要',
};

// 取組み課題のステータス表示。done は落ち着いた見た目 (outlined) にする
export const ISSUE_STATUS: Record<IssueStatus, { label: string; color: 'default' | 'info' | 'success' }> = {
  open: { label: '未着手', color: 'default' },
  in_progress: { label: '対応中', color: 'info' },
  done: { label: '完了', color: 'success' },
};

export const ISSUE_STATUS_ORDER: IssueStatus[] = ['open', 'in_progress', 'done'];

/** AI へ注入される定義か (バックエンドと同じ条件)。ワークフローは実行時に個別送信のため含めない */
export const isInjected = (a: Pick<AgentItem, 'type' | 'enabled' | 'status'>): boolean =>
  a.enabled !== false && (a.type === 'rule' || a.type === 'skill' || (a.type === 'issue' && a.status !== 'done'));

/** 1 定義が注入時に占める文字数 (名前 + 説明 + 本文) */
export const charsOf = (a: Pick<AgentItem, 'name' | 'description' | 'body'>): number =>
  (a.name?.length ?? 0) + (a.description?.length ?? 0) + (a.body?.length ?? 0);

/** 定義が「誰に見えるか」。グループ付きはグループの共有先、共通は定義自身の共有先 */
export function audienceOf(item: AgentItem, groupId: string, groupById: Map<string, AgentGroup>): string[] {
  if (groupId) {
    const g = groupById.get(groupId);
    if (!g) return [];
    return g.scope === 'shared' ? g.shared_with : [g.user_id];
  }
  return item.scope === 'shared' ? item.shared_with ?? [] : item.user_id ? [item.user_id] : [];
}

export function sameSet(a: string[], b: string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}

export interface StaffOption {
  email: string;
  name: string;
}

/** 共有メンバー候補 (MasterDataProvider のスタッフ一覧。staff_id=email)。本人は除く */
export function useStaffOptions(currentUserEmail: string): StaffOption[] {
  const { staffList } = useMasterData();
  return useMemo(
    () =>
      (staffList as { staff_id?: string; staff_name?: string }[])
        .map((s) => ({ email: (s.staff_id ?? '').trim(), name: (s.staff_name ?? '').trim() }))
        .filter((s) => s.email && s.email !== currentUserEmail)
        .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    [staffList, currentUserEmail]
  );
}

export const formatDateTime = (ms?: number): string => (ms ? new Date(ms).toLocaleString('ja-JP') : '');

// 画面全体で使う動きの共通値 (選択で右から滑り込む / 行が浮き上がる)
export const EASE_OUT: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

// --- 詳細ペインのヘッダー (戻る / 区分 / タイトル / 操作アイコン) ---------------------
interface DetailHeaderProps {
  kind: React.ReactNode; // 区分の小見出し (例 ルール ・ 1,200 字を注入)
  title: React.ReactNode; // 見出し (閲覧は文字列、編集は TextField)
  narrow: boolean; // 狭い幅では一覧へ戻るボタンを出す
  onBack: () => void;
  actions?: React.ReactNode;
}

export function DetailHeader({ kind, title, narrow, onBack, actions }: DetailHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      {narrow && (
        <IconButton size="small" onClick={onBack} aria-label="一覧へ戻る" sx={{ mt: 0.25 }}>
          <Icons.ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
          {kind}
        </Typography>
        {typeof title === 'string' ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.35, wordBreak: 'break-word' }}>
            {title}
          </Typography>
        ) : (
          title
        )}
      </Box>
      {actions && <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>{actions}</Box>}
    </Box>
  );
}

// --- 項目行 (ラベル + 値) -----------------------------------------------------
export function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 1, alignItems: 'center', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0, fontSize: '0.8rem' }}>
        {children}
      </Box>
    </Box>
  );
}

// --- 本文の閲覧枠 ---------------------------------------------------------------
export function BodyView({ label, body }: { label: string; body: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: blueGrey[600] }}>
        {label}
      </Typography>
      <Box
        sx={{
          whiteSpace: 'pre-wrap',
          fontSize: '0.8rem',
          lineHeight: 1.7,
          bgcolor: '#fff',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.75,
          py: 1.5,
          maxHeight: 360,
          overflow: 'auto',
          wordBreak: 'break-word',
        }}
      >
        {body || <Typography variant="caption" color="text.secondary">(本文なし)</Typography>}
      </Box>
    </Box>
  );
}

// --- 保存バー (変更があるときだけ下からせり上がる) -----------------------------------
interface SaveBarProps {
  show: boolean;
  message: string;
  dirty: boolean; // 未保存の変更があるか (脈動する点で示す。無い間は灰色の点)
  canSave: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}

export function SaveBar({ show, message, dirty, canSave, saving, onCancel, onSave }: SaveBarProps) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="savebar"
          initial={{ y: '110%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '110%', opacity: 0 }}
          transition={{ duration: 0.3, ease: EASE_OUT }}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.75,
              py: 1.25,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: '#fff',
              boxShadow: '0 -6px 16px rgba(0,0,0,.06)',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                component={motion.span}
                animate={dirty ? { scale: [1, 0.6, 1], opacity: [1, 0.5, 1] } : { scale: 1, opacity: 1 }}
                transition={dirty ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dirty ? 'warning.main' : 'divider', flexShrink: 0 }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {saving ? '保存しています...' : message}
              </Typography>
            </Box>
            <Tooltip title="Esc">
              <span>
                <Button size="small" onClick={onCancel} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
                  キャンセル
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Ctrl + Enter">
              <span>
                <Button
                  size="small"
                  onClick={onSave}
                  startIcon={<Icons.Save />}
                  disabled={!canSave || saving}
                  sx={STANDARD_CTA_BUTTON_SX}
                >
                  保存する
                </Button>
              </span>
            </Tooltip>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// --- 競合時の差分 (行単位。相手側に無い行を強調する) -----------------------------------
export function DiffLines({ text, other, label }: { text: string; other: string; label: string }) {
  const otherLines = useMemo(() => new Set(other.split('\n')), [other]);
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1,
        fontSize: '0.72rem',
        whiteSpace: 'pre-wrap',
        maxHeight: 140,
        overflow: 'auto',
        minWidth: 0,
        wordBreak: 'break-word',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.secondary', mb: 0.5, fontSize: '0.66rem' }}>
        {label}
      </Typography>
      {text.split('\n').map((line, i) => (
        <React.Fragment key={i}>
          {otherLines.has(line) ? (
            line
          ) : (
            <Box component="mark" sx={{ bgcolor: '#fff4e5', color: 'inherit', borderRadius: 0.5, px: 0.25 }}>
              {line || ' '}
            </Box>
          )}
          {'\n'}
        </React.Fragment>
      ))}
    </Box>
  );
}
